import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Topbar from "@/components/layout/Topbar";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
    Award, Users, Gift, Settings, Plus, Star, TrendingUp, X, Sparkles,
    ArrowUp, ArrowDown, Trash2, Crown,
} from "lucide-react";
import { toast } from "sonner";
import { formatCurrency, formatDateTime } from "@/lib/format";

const TAB_KEYS = [
    { key: "overview", icon: TrendingUp },
    { key: "members",  icon: Users },
    { key: "rewards",  icon: Gift },
    { key: "settings", icon: Settings },
];

const TIER_COLORS = {
    Bronze:   "bg-amber-50 text-amber-800 border-amber-200",
    Silver:   "bg-muted text-foreground border-border",
    Gold:     "bg-yellow-50 text-yellow-800 border-yellow-200",
    Platinum: "bg-violet-50 text-violet-800 border-violet-200",
};

export default function Loyalty() {
    const { t } = useTranslation();
    const [tab, setTab] = useState("overview");
    return (
        <>
            <Topbar title={t("loyalty.title")} subtitle={t("loyalty_page.subtitle")} />
            <div className="flex-1 overflow-auto p-4 md:p-6">
                <div className="mb-4 flex gap-1 border-b border-border" data-testid="loyalty-tabs">
                    {TAB_KEYS.map((tt) => {
                        const Icon = tt.icon;
                        return (
                            <button
                                key={tt.key}
                                onClick={() => setTab(tt.key)}
                                data-testid={`loyalty-tab-${tt.key}`}
                                className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-[12px] font-medium transition ${
                                    tab === tt.key
                                        ? "border-[#ff7b02] text-[#ff7b02]"
                                        : "border-transparent text-muted-foreground hover:text-foreground"
                                }`}
                            >
                                <Icon size={13} />
                                {t(`loyalty.tabs.${tt.key}`)}
                            </button>
                        );
                    })}
                </div>
                {tab === "overview" && <OverviewPane />}
                {tab === "members"  && <MembersPane />}
                {tab === "rewards"  && <RewardsPane />}
                {tab === "settings" && <SettingsPane />}
            </div>
        </>
    );
}

// ─────────────────────────────────────────────────────────────
// Overview
// ─────────────────────────────────────────────────────────────
function OverviewPane() {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [tiers, setTiers] = useState([]);
    useEffect(() => {
        (async () => {
            try {
                const [a, b] = await Promise.all([
                    api.get("/loyalty/overview"),
                    api.get("/loyalty/settings"),
                ]);
                setData(a.data);
                setTiers(b.data.tiers || []);
            } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        })();
    }, []);
    if (!data) return <div className="p-6 text-sm text-muted-foreground">{t("common.loading_ellipsis")}</div>;
    const cards = [
        { label: t("loyalty.members"),         value: data.members,           icon: Users, k: "members" },
        { label: t("loyalty.active_rewards"),  value: data.active_rewards,    icon: Gift, k: "active-rewards" },
        { label: t("loyalty.points_issued"),   value: data.points_issued.toLocaleString(),    icon: ArrowUp, k: "points-issued" },
        { label: t("loyalty.points_redeemed"), value: data.points_redeemed.toLocaleString(),  icon: ArrowDown, k: "points-redeemed" },
        { label: t("loyalty.outstanding"),     value: data.points_outstanding.toLocaleString(),icon: Sparkles, k: "outstanding" },
        { label: t("loyalty.redemptions"),     value: data.redemptions,       icon: Award, k: "redemptions" },
    ];
    return (
        <div className="space-y-6" data-testid="loyalty-overview">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
                {cards.map((c) => (
                    <div key={c.k} className="akm-card p-4">
                        <div className="flex items-center justify-between">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.label}</div>
                            <c.icon size={14} className="text-muted-foreground" />
                        </div>
                        <div className="mt-1 text-xl font-semibold tabular-nums" data-testid={`loyalty-kpi-${c.k}`}>
                            {c.value}
                        </div>
                    </div>
                ))}
            </div>

            <div className="akm-card overflow-hidden">
                <div className="border-b border-border bg-muted/30 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("loyalty.tier_ladder")}
                </div>
                <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-4">
                    {tiers.map((tier) => (
                        <div key={tier.name}
                             className={`rounded-md border p-3 ${TIER_COLORS[tier.name] || "border-border bg-card"}`}
                             data-testid={`loyalty-tier-${tier.name}`}>
                            <div className="flex items-center gap-1.5 text-[13px] font-semibold">
                                <Crown size={13} /> {tier.name}
                            </div>
                            <div className="mt-1 text-[11px]">
                                {tier.min_lifetime_points.toLocaleString()}+ pts · {tier.discount_pct}% off
                            </div>
                            <div className="mt-2 flex flex-wrap gap-1">
                                {(tier.perks || []).map((p) => (
                                    <span key={p} className="rounded-full bg-white/70 px-2 py-0.5 text-[10px]">{p}</span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// Members
// ─────────────────────────────────────────────────────────────
function MembersPane() {
    const [rows, setRows] = useState([]);
    const [q, setQ] = useState("");
    const [tier, setTier] = useState("");
    const [active, setActive] = useState(null);

    const load = async () => {
        try {
            const params = new URLSearchParams();
            if (q) params.set("q", q);
            if (tier) params.set("tier", tier);
            const { data } = await api.get(`/loyalty/members?${params.toString()}`);
            setRows(data);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [tier]);

    return (
        <div className="space-y-3" data-testid="loyalty-members">
            <div className="flex flex-wrap items-center gap-2">
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && load()}
                    placeholder="Search by name or email…"
                    className="akm-input w-72 text-[13px]"
                    data-testid="loyalty-search"
                />
                <select value={tier} onChange={(e) => setTier(e.target.value)} className="akm-input text-[13px]"
                        data-testid="loyalty-tier-filter">
                    <option value="">All tiers</option>
                    <option>Bronze</option><option>Silver</option><option>Gold</option><option>Platinum</option>
                </select>
                <button onClick={load} className="akm-btn-ghost text-[12px]">Search</button>
            </div>

            <div className="akm-card overflow-hidden">
                <table className="w-full text-[13px]">
                    <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-3 py-2 text-left">Member</th>
                            <th className="px-3 py-2 text-left">Tier</th>
                            <th className="px-3 py-2 text-right">Balance</th>
                            <th className="px-3 py-2 text-right">Lifetime earned</th>
                            <th className="px-3 py-2 text-right">Stays</th>
                            <th className="px-3 py-2 text-right">Lifetime value</th>
                            <th className="px-3 py-2" />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} className="border-t border-border hover:bg-muted/30">
                                <td className="px-3 py-2">
                                    <div className="font-medium flex items-center gap-1.5">
                                        {r.vip && <Star size={11} className="fill-amber-400 text-amber-400" />}
                                        {r.first_name} {r.last_name}
                                    </div>
                                    <div className="text-[11px] text-muted-foreground">{r.email || "—"}</div>
                                </td>
                                <td className="px-3 py-2">
                                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${TIER_COLORS[r.tier] || ""}`}>
                                        {r.tier}
                                    </span>
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums font-semibold">{r.balance.toLocaleString()}</td>
                                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{r.lifetime_earned.toLocaleString()}</td>
                                <td className="px-3 py-2 text-right tabular-nums">{r.total_stays}</td>
                                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{formatCurrency(r.lifetime_value || 0)}</td>
                                <td className="px-3 py-2 text-right">
                                    <button onClick={() => setActive(r.id)} className="akm-btn-ghost text-[11px]"
                                            data-testid={`loyalty-open-${r.id}`}>Manage</button>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr><td colSpan="7" className="py-10 text-center text-muted-foreground">No members</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            {active && <MemberDrawer id={active} onClose={() => setActive(null)} onChanged={load} />}
        </div>
    );
}

function MemberDrawer({ id, onClose, onChanged }) {
    const [data, setData] = useState(null);
    const [rewards, setRewards] = useState([]);
    const [adjustPts, setAdjustPts] = useState(0);
    const [adjustReason, setAdjustReason] = useState("");
    const [selectedReward, setSelectedReward] = useState("");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const [a, b] = await Promise.all([
                api.get(`/loyalty/members/${id}`),
                api.get("/loyalty/rewards"),
            ]);
            setData(a.data);
            setRewards(b.data.filter((x) => x.active));
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const adjust = async () => {
        if (!adjustPts) return toast.error("Points required");
        setBusy(true);
        try {
            await api.post(`/loyalty/members/${id}/adjust`, {
                points: Number(adjustPts), reason: adjustReason,
            });
            toast.success("Adjusted");
            setAdjustPts(0); setAdjustReason("");
            await load(); await onChanged();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const redeem = async () => {
        if (!selectedReward) return toast.error("Select a reward");
        setBusy(true);
        try {
            await api.post(`/loyalty/members/${id}/redeem`, { reward_id: selectedReward });
            toast.success("Reward redeemed");
            setSelectedReward("");
            await load(); await onChanged();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const toggleVip = async () => {
        setBusy(true);
        try {
            await api.post(`/loyalty/members/${id}/vip`, { vip: !data.guest.vip });
            await load(); await onChanged();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex" data-testid="loyalty-drawer">
            <div className="flex-1 bg-foreground/30" onClick={onClose} />
            <div className="flex h-full w-full max-w-[680px] flex-col overflow-y-auto border-l border-border bg-background shadow-2xl">
                <div className="flex items-center justify-between border-b border-border px-5 py-3">
                    <div>
                        <div className="text-[13px] font-semibold tracking-tight">Loyalty member</div>
                        <div className="text-[11px] text-muted-foreground">Points · Tier · Rewards</div>
                    </div>
                    <button onClick={onClose} className="text-muted-foreground"><X size={16} /></button>
                </div>
                {!data ? <div className="p-6 text-sm text-muted-foreground">Loading…</div> : (
                    <div className="p-5 space-y-4">
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="text-base font-semibold">{data.guest.first_name} {data.guest.last_name}</div>
                                <div className="text-[12px] text-muted-foreground">{data.guest.email || "—"}</div>
                            </div>
                            <div className="text-right">
                                <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${TIER_COLORS[data.tier.name]}`}>
                                    <Crown size={11} className="inline -mt-px mr-1" />
                                    {data.tier.name}
                                </span>
                                <div className="mt-2 text-2xl font-semibold tabular-nums text-[#ff7b02]" data-testid="loyalty-balance">
                                    {data.balance.toLocaleString()} <span className="text-[11px] text-muted-foreground font-normal">pts</span>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-3 gap-2 text-center">
                            <KpiBox label="Lifetime earned" value={data.lifetime_earned.toLocaleString()} />
                            <KpiBox label="Redeemed"        value={data.redeemed.toLocaleString()} />
                            <KpiBox label="Stays"           value={data.guest.total_stays} />
                        </div>

                        <div className="rounded-md border border-border p-3">
                            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Quick actions</div>
                            <button onClick={toggleVip} disabled={busy}
                                    className="akm-btn-ghost text-[12px]"
                                    data-testid="loyalty-vip-toggle">
                                <Star size={12} className={data.guest.vip ? "fill-amber-400 text-amber-400" : ""} />
                                {data.guest.vip ? "Remove VIP" : "Mark as VIP"}
                            </button>
                        </div>

                        <div className="rounded-md border border-border p-3">
                            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Adjust points</div>
                            <div className="flex gap-2">
                                <input type="number" placeholder="e.g. 500 or -100"
                                       value={adjustPts}
                                       onChange={(e) => setAdjustPts(e.target.value)}
                                       className="akm-input w-32 text-[13px]"
                                       data-testid="loyalty-adjust-points" />
                                <input value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)}
                                       placeholder="Reason (optional)" className="akm-input flex-1 text-[13px]"
                                       data-testid="loyalty-adjust-reason" />
                                <button onClick={adjust} disabled={busy} className="akm-btn-primary text-[12px]"
                                        data-testid="loyalty-adjust-submit">Apply</button>
                            </div>
                        </div>

                        <div className="rounded-md border border-border p-3">
                            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Redeem reward</div>
                            {rewards.length === 0 ? (
                                <div className="text-[12px] text-muted-foreground">No active rewards. Create one under the Rewards tab.</div>
                            ) : (
                                <div className="flex gap-2">
                                    <select value={selectedReward} onChange={(e) => setSelectedReward(e.target.value)}
                                            className="akm-input flex-1 text-[13px]" data-testid="loyalty-reward-select">
                                        <option value="">— Select reward —</option>
                                        {rewards.map((r) => (
                                            <option key={r.id} value={r.id} disabled={r.points_cost > data.balance}>
                                                {r.name} · {r.points_cost} pts · min {r.min_tier}
                                            </option>
                                        ))}
                                    </select>
                                    <button onClick={redeem} disabled={busy || !selectedReward}
                                            className="akm-btn-primary text-[12px]"
                                            data-testid="loyalty-redeem-submit">Redeem</button>
                                </div>
                            )}
                        </div>

                        <div className="rounded-md border border-border overflow-hidden">
                            <div className="border-b border-border bg-muted/30 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                                Ledger ({data.transactions})
                            </div>
                            <table className="w-full text-[12.5px]">
                                <tbody data-testid="loyalty-ledger">
                                    {(data.ledger || []).map((l) => (
                                        <tr key={l.id} className="border-t border-border">
                                            <td className="px-3 py-1.5 text-[11px] text-muted-foreground">
                                                {formatDateTime(l.created_at)}
                                            </td>
                                            <td className="px-3 py-1.5">
                                                <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wider">{l.kind}</span>
                                            </td>
                                            <td className="px-3 py-1.5">{l.reason}</td>
                                            <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${l.points > 0 ? "text-emerald-700" : "text-red-700"}`}>
                                                {l.points > 0 ? "+" : ""}{l.points}
                                            </td>
                                        </tr>
                                    ))}
                                    {(!data.ledger || data.ledger.length === 0) && (
                                        <tr><td colSpan="4" className="py-6 text-center text-[12px] text-muted-foreground">No transactions yet</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function KpiBox({ label, value }) {
    return (
        <div className="rounded-md border border-border p-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="mt-0.5 text-[14px] font-semibold tabular-nums">{value}</div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// Rewards
// ─────────────────────────────────────────────────────────────
function RewardsPane() {
    const [rows, setRows] = useState([]);
    const [form, setForm] = useState({
        name: "", description: "", kind: "perk", value: 0, points_cost: 500,
        min_tier: "Bronze", stock: 0, active: true,
    });
    const [showForm, setShowForm] = useState(false);

    const load = async () => {
        try { setRows((await api.get("/loyalty/rewards")).data); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    useEffect(() => { load(); }, []);

    const create = async () => {
        try {
            await api.post("/loyalty/rewards", form);
            toast.success("Reward created");
            setShowForm(false);
            setForm({ name: "", description: "", kind: "perk", value: 0, points_cost: 500,
                       min_tier: "Bronze", stock: 0, active: true });
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const toggle = async (r) => {
        await api.patch(`/loyalty/rewards/${r.id}`, { active: !r.active });
        await load();
    };
    const del = async (id) => {
        if (!window.confirm("Delete reward?")) return;
        await api.delete(`/loyalty/rewards/${id}`);
        await load();
    };

    return (
        <div className="space-y-3" data-testid="loyalty-rewards">
            <div className="flex items-center justify-between">
                <div className="text-[12px] text-muted-foreground">Rewards your members can redeem with points.</div>
                <button onClick={() => setShowForm((s) => !s)} className="akm-btn-primary text-[12px]"
                        data-testid="loyalty-reward-new">
                    <Plus size={12} /> New reward
                </button>
            </div>

            {showForm && (
                <div className="akm-card p-4 space-y-2" data-testid="loyalty-reward-form">
                    <div className="grid grid-cols-2 gap-2">
                        <Input label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="lr-name" />
                        <Select label="Kind" value={form.kind} onChange={(v) => setForm({ ...form, kind: v })}
                                options={[
                                    { v: "perk",          l: "Perk (free item)" },
                                    { v: "discount_pct",  l: "Discount %" },
                                    { v: "fixed_amount",  l: "Fixed €" },
                                    { v: "free_night",    l: "Free night" },
                                ]} />
                        <Input label="Value" type="number" value={form.value} onChange={(v) => setForm({ ...form, value: v })} testid="lr-value" />
                        <Input label="Points cost" type="number" value={form.points_cost} onChange={(v) => setForm({ ...form, points_cost: v })} testid="lr-cost" />
                        <Select label="Min tier" value={form.min_tier} onChange={(v) => setForm({ ...form, min_tier: v })}
                                options={["Bronze", "Silver", "Gold", "Platinum"].map((x) => ({ v: x, l: x }))} />
                        <Input label="Stock (0 = unlimited)" type="number" value={form.stock} onChange={(v) => setForm({ ...form, stock: v })} testid="lr-stock" />
                    </div>
                    <Input label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="lr-desc" />
                    <div className="flex justify-end gap-2">
                        <button onClick={() => setShowForm(false)} className="akm-btn-ghost text-[12px]">Cancel</button>
                        <button onClick={create} className="akm-btn-primary text-[12px]" data-testid="loyalty-reward-save">Create</button>
                    </div>
                </div>
            )}

            <div className="akm-card overflow-hidden">
                <table className="w-full text-[13px]">
                    <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-3 py-2 text-left">Name</th>
                            <th className="px-3 py-2 text-left">Kind</th>
                            <th className="px-3 py-2 text-right">Value</th>
                            <th className="px-3 py-2 text-right">Cost</th>
                            <th className="px-3 py-2 text-left">Min tier</th>
                            <th className="px-3 py-2 text-right">Stock</th>
                            <th className="px-3 py-2 text-center">Active</th>
                            <th className="px-3 py-2" />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} className="border-t border-border hover:bg-muted/30">
                                <td className="px-3 py-2 font-medium">{r.name}<div className="text-[10px] text-muted-foreground">{r.description}</div></td>
                                <td className="px-3 py-2 text-muted-foreground">{r.kind}</td>
                                <td className="px-3 py-2 text-right tabular-nums">{r.value}</td>
                                <td className="px-3 py-2 text-right tabular-nums font-semibold">{r.points_cost}</td>
                                <td className="px-3 py-2">
                                    <span className={`rounded-full border px-2 py-0.5 text-[10px] ${TIER_COLORS[r.min_tier] || ""}`}>{r.min_tier}</span>
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums">{r.stock || "∞"}</td>
                                <td className="px-3 py-2 text-center">
                                    <button onClick={() => toggle(r)}
                                            className={`h-4 w-8 rounded-full transition-colors ${r.active ? "bg-[#ff7b02]" : "bg-muted"}`}
                                            data-testid={`lr-toggle-${r.id}`}>
                                        <span className={`block h-3 w-3 rounded-full bg-white shadow transition-transform ${r.active ? "translate-x-4" : "translate-x-0.5"}`} />
                                    </button>
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <button onClick={() => del(r.id)} className="text-red-700" data-testid={`lr-del-${r.id}`}>
                                        <Trash2 size={13} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr><td colSpan="8" className="py-10 text-center text-muted-foreground">No rewards yet</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// Settings
// ─────────────────────────────────────────────────────────────
function SettingsPane() {
    const [s, setS] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        (async () => {
            try { setS((await api.get("/loyalty/settings")).data); }
            catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        })();
    }, []);

    const save = async () => {
        setBusy(true);
        try {
            await api.put("/loyalty/settings", s);
            toast.success("Settings saved");
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    if (!s) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

    const set = (k, v) => setS({ ...s, [k]: v });
    const setTier = (i, k, v) => {
        const t = [...s.tiers];
        t[i] = { ...t[i], [k]: v };
        setS({ ...s, tiers: t });
    };

    return (
        <div className="space-y-4" data-testid="loyalty-settings">
            <div className="akm-card p-4 space-y-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Earning rules</div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <div className="flex items-center gap-2">
                        <input type="checkbox" checked={s.enabled} onChange={(e) => set("enabled", e.target.checked)}
                               data-testid="ls-enabled" />
                        <span className="text-[13px]">Loyalty enabled</span>
                    </div>
                    <Input label="Points per €1 spent" type="number" value={s.currency_to_points}
                           onChange={(v) => set("currency_to_points", Number(v))} testid="ls-rate" />
                    <Input label="Direct-booking multiplier" type="number" step="0.1" value={s.direct_booking_multiplier}
                           onChange={(v) => set("direct_booking_multiplier", Number(v))} testid="ls-mult" />
                    <Input label="Expiry (months)" type="number" value={s.points_expiry_months}
                           onChange={(v) => set("points_expiry_months", Number(v))} testid="ls-expiry" />
                </div>
            </div>

            <div className="akm-card p-4">
                <div className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Tier ladder</div>
                <table className="w-full text-[13px]">
                    <thead className="text-[11px] uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="text-left py-1">Name</th>
                            <th className="text-right py-1">Min lifetime pts</th>
                            <th className="text-right py-1">Discount %</th>
                            <th className="text-left py-1">Perks (comma)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {s.tiers.map((t, i) => (
                            <tr key={i} className="border-t border-border">
                                <td className="py-1">
                                    <input value={t.name} onChange={(e) => setTier(i, "name", e.target.value)}
                                           className="akm-input text-[12.5px] w-28"
                                           data-testid={`ls-tier-name-${i}`} />
                                </td>
                                <td className="py-1 text-right">
                                    <input type="number" value={t.min_lifetime_points}
                                           onChange={(e) => setTier(i, "min_lifetime_points", Number(e.target.value))}
                                           className="akm-input text-[12.5px] w-28 text-right" />
                                </td>
                                <td className="py-1 text-right">
                                    <input type="number" value={t.discount_pct}
                                           onChange={(e) => setTier(i, "discount_pct", Number(e.target.value))}
                                           className="akm-input text-[12.5px] w-20 text-right" />
                                </td>
                                <td className="py-1">
                                    <input value={(t.perks || []).join(", ")}
                                           onChange={(e) => setTier(i, "perks", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))}
                                           className="akm-input text-[12.5px] w-full" />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex justify-end">
                <button onClick={save} disabled={busy} className="akm-btn-primary text-[12px]" data-testid="ls-save">
                    Save settings
                </button>
            </div>
        </div>
    );
}

function Input({ label, value, onChange, type = "text", step, testid }) {
    return (
        <label className="block">
            <div className="text-[11px] font-medium text-muted-foreground mb-1">{label}</div>
            <input type={type} step={step} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
                   className="akm-input w-full text-[13px]" data-testid={testid} />
        </label>
    );
}
function Select({ label, value, onChange, options }) {
    return (
        <label className="block">
            <div className="text-[11px] font-medium text-muted-foreground mb-1">{label}</div>
            <select value={value} onChange={(e) => onChange(e.target.value)} className="akm-input w-full text-[13px]">
                {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
        </label>
    );
}
